import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api as axios } from '../utils/apiClient';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import RiskMeter from './security/RiskMeter';
import WebsiteSummaryCard from './security/WebsiteSummaryCard';
import SecurityStatusCard from './security/SecurityStatusCard';
import BlacklistStatusCard from './security/BlacklistStatusCard';
import HardeningImprovementsCard from './security/HardeningImprovementsCard';
import OutdatedSoftwareCard from './security/OutdatedSoftwareCard';
import SummaryBanner from './security/SummaryBanner';
import { transformToSucuriFormat } from './security/transformScanData';
import './SecurityMonitoring.css';
import { useLocation, useNavigate } from 'react-router-dom';

const SecurityMonitoring = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [securityData, setSecurityData] = useState({});
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState({});
  const [error, setError] = useState(null);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [forceSyncTriggered, setForceSyncTriggered] = useState(false);
  const [forceSyncInProgress, setForceSyncInProgress] = useState(false);
  const forceParam = new URLSearchParams(location.search).get('force') === 'true';
  const siteIdParam = new URLSearchParams(location.search).get('siteId');
  // AppSec / DAST data for Invicti-style sections (scan overview, vulns, proofs)
  const [appsecScans, setAppsecScans] = useState([]);
  const [appsecVulns, setAppsecVulns] = useState([]);
  const [appsecLoading, setAppsecLoading] = useState(false);
  const [appsecError, setAppsecError] = useState(null);
  const [appsecTab, setAppsecTab] = useState('overview'); // 'overview'

  const fetchSites = useCallback(async (forceRefresh = false) => {
    try {
      console.log('[SecurityMonitoring] Fetching sites list');
      const { data } = await axios.get('/api/sites');
      setSites(data);
      console.log('[SecurityMonitoring] Sites loaded', data.map(site => ({ id: site._id, url: site.url })));
      
      // Don't auto-select - show all sites by default
      // Only clear selection if previously selected site no longer exists
      if (data.length > 0 && selectedSiteId && !data.find(s => s._id === selectedSiteId)) {
        setSelectedSiteId(''); // Clear selection to show all sites
      }
      
      // INSTANT: Show existing cached data immediately (no loading spinner)
      // Only show loading if we have NO cached data at all
      if (Object.keys(securityData).length === 0) {
        setLoading(true);
      } else {
        // We have cached data - hide loading immediately and show it
        setLoading(false);
      }
      
      // Reasonable timeout for cached data (10 seconds - enough for slow networks and backend processing)
      const getWithTimeout = async (url, baseMs = 10000) => {
        return Promise.race([
          axios.get(url, { timeout: baseMs }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), baseMs))
        ]);
      };

      // PROGRESSIVE RENDERING: Update each site as its data arrives (don't wait for all)
      data.forEach((site) => {
        // Get cached data with reasonable timeout (10 seconds for slow backends)
        const endpoint = `/api/sites/${site._id}/monitor`;
        console.log('[SecurityMonitoring] Fetching cached security data', { siteId: site._id, endpoint });
        
        // Fire and update progressively - don't wait for all
        getWithTimeout(endpoint, 10000).then(async (monitorResponse) => {
          const finalPayload = monitorResponse.data;
          
          // Check if we have critical meta
          const hasIPInitial = !!(finalPayload?.data?.ip || finalPayload?.data?.ip_address || finalPayload?.transformedData?.ip || finalPayload?.transformedData?.ip_address);
          const hasHostingInitial = !!(finalPayload?.data?.hosting || finalPayload?.transformedData?.hosting);
          const hasRunningOnInitial = !!(finalPayload?.data?.runningOn || finalPayload?.data?.server || finalPayload?.transformedData?.runningOn || finalPayload?.transformedData?.server);

          // If missing critical meta, trigger background refresh (non-blocking)
          if (!hasIPInitial || !hasHostingInitial || !hasRunningOnInitial) {
            // Don't wait - return cached data immediately, refresh in background
            const forceEndpoint = `/api/sites/${site._id}/monitor?force=true`;
            console.log('[SecurityMonitoring] Missing meta, will refresh in background', { siteId: site._id });
            // Fire and forget - don't await
            getWithTimeout(forceEndpoint, 5000).then(forceResponse => {
              const forcePayload = forceResponse.data;
              // Update securityData with fresh data when it arrives
              setSecurityData(prev => ({
                ...prev,
                [site._id]: {
                  ...prev[site._id],
                  rawData: forcePayload?.data || prev[site._id]?.rawData,
                  transformedData: forcePayload?.transformedData || prev[site._id]?.transformedData,
                  status: forcePayload?.status || 'live'
                }
              }));
            }).catch(() => {
              // Background refresh failed, keep cached data
            });
          }
          console.log('[SecurityMonitoring] Monitor payload', { siteId: site._id, payload: finalPayload, hasData: !!finalPayload?.data, hasDirectData: !!(finalPayload?.wp_version || finalPayload?.php_version) });
          
          // Helper to clean error messages
          const cleanError = (msg) => {
            if (!msg) return null;
            let cleaned = msg.toString();
            cleaned = cleaned.replace(/HTTP\s+\d{3}:\s*[^\n]*/gi, '').trim();
            cleaned = cleaned.replace(/^\d{3}\s*[^\n]*/gi, '').trim();
            cleaned = cleaned.replace(/^OK$/i, '').trim();
            return cleaned || null;
          };
          
          // Extract site data from multiple possible locations
          let siteData = finalPayload?.data || finalPayload;
          const transformedFromApi = finalPayload?.transformedData || null;
          
          // Check if we have ANY meaningful data (meta OR core fields OR transformed data)
          const hasMetaData = siteData && (siteData.ip || siteData.ip_address || siteData.hosting || siteData.runningOn || siteData.server || siteData.cms || siteData.powered_by);
          const hasCoreFields = siteData && (siteData.wp_version || siteData.php_version || siteData.plugins || siteData.active_theme);
          const hasTransformedData = transformedFromApi && (transformedFromApi.ip || transformedFromApi.hosting || transformedFromApi.runningOn || transformedFromApi.cms);
          const hasAnyData = hasMetaData || hasCoreFields || hasTransformedData || (siteData && typeof siteData === 'object' && Object.keys(siteData).length > 0);
          
          // Only show error if we truly have NO data at all
          // Even if success is false, if we have data, we should display it
          if (!hasAnyData && finalPayload?.success === false) {
            const errorMsg = cleanError(finalPayload.error) || cleanError(finalPayload.message) || 'Unable to load site details';
            setSecurityData(prev => ({
              ...prev,
              [site._id]: { data: null, rawData: null, transformedData: null, error: errorMsg, status: 'unavailable' }
            }));
            return;
          }
          
          // If we have no siteData object but have transformedData, create a minimal siteData
          if (!siteData && transformedFromApi) {
            // Use transformedData as the base data
            siteData = {
              ip: transformedFromApi.ip || transformedFromApi.ip_address || '',
              ip_address: transformedFromApi.ip_address || transformedFromApi.ip || '',
              hosting: transformedFromApi.hosting || '',
              runningOn: transformedFromApi.runningOn || transformedFromApi.server || '',
              server: transformedFromApi.server || transformedFromApi.runningOn || '',
              cms: transformedFromApi.cms || '',
              powered_by: transformedFromApi.powered_by || transformedFromApi.poweredBy || ''
            };
          }
          
          // If siteData is still empty/null but we have transformedData, ensure we have something to work with
          if ((!siteData || (typeof siteData === 'object' && Object.keys(siteData).length === 0)) && transformedFromApi) {
            siteData = siteData || {};
            // Merge transformedData into siteData for processing
            siteData.ip = siteData.ip || transformedFromApi.ip || transformedFromApi.ip_address || '';
            siteData.ip_address = siteData.ip_address || transformedFromApi.ip_address || transformedFromApi.ip || '';
            siteData.hosting = siteData.hosting || transformedFromApi.hosting || '';
            siteData.runningOn = siteData.runningOn || transformedFromApi.runningOn || transformedFromApi.server || '';
            siteData.server = siteData.server || transformedFromApi.server || transformedFromApi.runningOn || '';
            siteData.cms = siteData.cms || transformedFromApi.cms || '';
            siteData.powered_by = siteData.powered_by || transformedFromApi.powered_by || transformedFromApi.poweredBy || '';
          }
          
          // Process the data
          const securityInfo = performSecurityChecks(site, siteData);
          const warningError = finalPayload?.error ? cleanError(finalPayload.error) : null;
          
          // Extract meta from all sources
          const ipFromPayload = finalPayload?.ip || finalPayload?.ip_address || '';
          const hostingFromPayload = finalPayload?.hosting || '';
          const runningOnFromPayload = finalPayload?.runningOn || finalPayload?.server || '';
          const ipFromData = siteData?.ip || siteData?.ip_address || ipFromPayload || '';
          const hostingFromData = siteData?.hosting || hostingFromPayload || '';
          const runningOnFromData = siteData?.runningOn || siteData?.server || runningOnFromPayload || '';
          const ipFromTransformed = transformedFromApi?.ip || transformedFromApi?.ip_address || '';
          const hostingFromTransformed = transformedFromApi?.hosting || '';
          const runningOnFromTransformed = transformedFromApi?.runningOn || transformedFromApi?.server || '';

          const sucuriData = transformedFromApi || transformToSucuriFormat(site, siteData, securityInfo);
          
          // Ensure all meta fields are populated
          if (sucuriData) {
            sucuriData.ip = sucuriData.ip || sucuriData.ip_address || ipFromTransformed || ipFromData || ipFromPayload || '';
            sucuriData.ip_address = sucuriData.ip_address || sucuriData.ip || ipFromTransformed || ipFromData || ipFromPayload || '';
            sucuriData.hosting = sucuriData.hosting || hostingFromTransformed || hostingFromData || hostingFromPayload || '';
            sucuriData.runningOn = sucuriData.runningOn || sucuriData.server || runningOnFromTransformed || runningOnFromData || runningOnFromPayload || '';
            sucuriData.server = sucuriData.server || sucuriData.runningOn || runningOnFromTransformed || runningOnFromData || runningOnFromPayload || '';
            sucuriData.cms = sucuriData.cms || siteData?.cms || finalPayload?.cms || '';
            sucuriData.powered_by = sucuriData.powered_by || sucuriData.poweredBy || siteData?.powered_by || finalPayload?.powered_by || finalPayload?.poweredBy || '';
          }
          
          // CRITICAL: NEVER show errors if we have ANY valid data
          // Suppress ALL errors if we have meta data, core fields, or transformed data
          let displayError = null; // Default to no error
          
          // Only show error if we have NO data at all
          const hasAnyValidData = !!(sucuriData?.ip || sucuriData?.hosting || sucuriData?.runningOn || sucuriData?.cms || 
                                      siteData?.ip || siteData?.ip_address || siteData?.hosting || siteData?.runningOn || 
                                      siteData?.wp_version || siteData?.php_version || siteData?.cms || siteData?.plugins || siteData?.active_theme);
          
          // If we have ANY data, NEVER show error
          if (!hasAnyValidData && warningError) {
            // Only show error if we truly have no data AND there's a warning
            displayError = warningError;
          }
          
          // Additional check: if we have meta data or transformed data, suppress error completely
          if (hasMetaData || hasTransformedData || hasAnyValidData) {
            displayError = null; // Force no error if we have any data
          }
          
          // Update UI immediately for this site (progressive rendering)
          setSecurityData(prev => ({
            ...prev,
            [site._id]: {
              data: securityInfo,
              rawData: siteData,
              transformedData: sucuriData,
              lastScan: finalPayload?.lastSync || finalPayload?.lastScan || new Date().toISOString(),
              error: displayError,
              status: finalPayload?.status || (finalPayload?.cached ? 'cached' : 'live')
            }
          }));
          
          // Check if we need background refresh for missing meta
          const hasIP = !!(sucuriData?.ip || sucuriData?.ip_address);
          const hasHosting = !!sucuriData?.hosting;
          const hasRunningOn = !!(sucuriData?.runningOn || sucuriData?.server);
          
          if (!hasIP || !hasHosting || !hasRunningOn) {
            // Background refresh (non-blocking, 5s timeout)
            const forceEndpoint = `/api/sites/${site._id}/monitor?force=true`;
            axios.get(forceEndpoint, { timeout: 5000 }).then(forceResponse => {
              const forcePayload = forceResponse.data;
              const forceSiteData = forcePayload?.data || forcePayload;
              const forceTransformed = forcePayload?.transformedData || null;
              const forceSecurityInfo = performSecurityChecks(site, forceSiteData);
              const forceSucuriData = forceTransformed || transformToSucuriFormat(site, forceSiteData, forceSecurityInfo);
              
              // Extract and merge meta from force refresh
              const forceIp = forceSucuriData?.ip || forceSucuriData?.ip_address || forceSiteData?.ip || forceSiteData?.ip_address || '';
              const forceHosting = forceSucuriData?.hosting || forceSiteData?.hosting || '';
              const forceRunningOn = forceSucuriData?.runningOn || forceSucuriData?.server || forceSiteData?.runningOn || forceSiteData?.server || '';
              
              if (forceSucuriData) {
                forceSucuriData.ip = forceIp || forceSucuriData.ip || '';
                forceSucuriData.ip_address = forceIp || forceSucuriData.ip_address || '';
                forceSucuriData.hosting = forceHosting || forceSucuriData.hosting || '';
                forceSucuriData.runningOn = forceRunningOn || forceSucuriData.runningOn || forceSucuriData.server || '';
                forceSucuriData.server = forceRunningOn || forceSucuriData.server || forceSucuriData.runningOn || '';
                forceSucuriData.cms = forceSucuriData.cms || forceSiteData?.cms || '';
                forceSucuriData.powered_by = forceSucuriData.powered_by || forceSucuriData.poweredBy || forceSiteData?.powered_by || '';
              }
              
              // Update with fresh data when it arrives
              setSecurityData(prev => ({
                ...prev,
                [site._id]: {
                  ...prev[site._id],
                  rawData: forceSiteData,
                  transformedData: forceSucuriData,
                  status: 'live'
                }
              }));
            }).catch(() => {
              // Background refresh failed, keep cached data
            });
          }
        }).catch((err) => {
          // Timeout or error - check multiple sources for data before showing error
          const existingData = securityData[site._id];
          
          // Check if error response has data (sometimes axios returns data even on timeout)
          const errorResponseData = err.response?.data;
          const hasErrorResponseData = errorResponseData && (
            errorResponseData.data || 
            errorResponseData.transformedData || 
            errorResponseData.ip || 
            errorResponseData.hosting ||
            (errorResponseData.data && (errorResponseData.data.ip || errorResponseData.data.hosting || errorResponseData.data.runningOn))
          );
          
          // CRITICAL: Try silent retry with longer timeout before showing error
          // This handles cases where backend is slow but has cached data
          if (!existingData && !hasErrorResponseData) {
            console.log('[SecurityMonitoring] First request failed, attempting silent retry with longer timeout', { siteId: site._id });
            // Silent retry with 15 second timeout - don't show error yet
            axios.get(`/api/sites/${site._id}/monitor`, { timeout: 15000 })
              .then(retryResponse => {
                const retryPayload = retryResponse.data;
                let retrySiteData = retryPayload?.data || retryPayload;
                const retryTransformed = retryPayload?.transformedData || null;
                
                const hasRetryMeta = retrySiteData && (retrySiteData.ip || retrySiteData.ip_address || retrySiteData.hosting || retrySiteData.runningOn || retrySiteData.server || retrySiteData.cms);
                const hasRetryTransformed = retryTransformed && (retryTransformed.ip || retryTransformed.hosting || retryTransformed.runningOn || retryTransformed.cms);
                
                if (hasRetryMeta || hasRetryTransformed || retryPayload?.cached) {
                  // Retry succeeded - process data using same logic as success case
                  console.log('[SecurityMonitoring] Silent retry succeeded, processing data', { siteId: site._id });
                  
                  // Process retry data (duplicate success logic)
                  if (!retrySiteData && retryTransformed) {
                    retrySiteData = {
                      ip: retryTransformed.ip || retryTransformed.ip_address || '',
                      ip_address: retryTransformed.ip_address || retryTransformed.ip || '',
                      hosting: retryTransformed.hosting || '',
                      runningOn: retryTransformed.runningOn || retryTransformed.server || '',
                      server: retryTransformed.server || retryTransformed.runningOn || '',
                      cms: retryTransformed.cms || '',
                      powered_by: retryTransformed.powered_by || retryTransformed.poweredBy || ''
                    };
                  }
                  
                  const retrySecurityInfo = performSecurityChecks(site, retrySiteData || {});
                  const retrySucuriData = retryTransformed || transformToSucuriFormat(site, retrySiteData, retrySecurityInfo);
                  
                  // Extract meta from all sources
                  const retryIpFromData = retrySiteData?.ip || retrySiteData?.ip_address || retryPayload?.ip || '';
                  const retryHostingFromData = retrySiteData?.hosting || retryPayload?.hosting || '';
                  const retryRunningOnFromData = retrySiteData?.runningOn || retrySiteData?.server || retryPayload?.runningOn || retryPayload?.server || '';
                  
                  if (retrySucuriData) {
                    retrySucuriData.ip = retrySucuriData.ip || retrySucuriData.ip_address || retryTransformed?.ip || retryIpFromData || '';
                    retrySucuriData.ip_address = retrySucuriData.ip_address || retrySucuriData.ip || retryTransformed?.ip_address || retryIpFromData || '';
                    retrySucuriData.hosting = retrySucuriData.hosting || retryTransformed?.hosting || retryHostingFromData || '';
                    retrySucuriData.runningOn = retrySucuriData.runningOn || retrySucuriData.server || retryTransformed?.runningOn || retryRunningOnFromData || '';
                    retrySucuriData.server = retrySucuriData.server || retrySucuriData.runningOn || retryTransformed?.server || retryRunningOnFromData || '';
                    retrySucuriData.cms = retrySucuriData.cms || retrySiteData?.cms || retryPayload?.cms || '';
                    retrySucuriData.powered_by = retrySucuriData.powered_by || retrySucuriData.poweredBy || retrySiteData?.powered_by || retryPayload?.powered_by || '';
                  }
                  
                  // Update state with retry data - NO ERROR
                  setSecurityData(prev => ({
                    ...prev,
                    [site._id]: {
                      data: retrySecurityInfo,
                      rawData: retrySiteData,
                      transformedData: retrySucuriData,
                      lastScan: retryPayload?.lastSync || retryPayload?.lastScan || new Date().toISOString(),
                      error: null, // NO ERROR - we have data
                      status: retryPayload?.status || (retryPayload?.cached ? 'cached' : 'live')
                    }
                  }));
                  return; // Exit - data loaded successfully
                }
              })
              .catch(() => {
                // Retry also failed - will fall through to delayed error handling below
                console.log('[SecurityMonitoring] Silent retry also failed', { siteId: site._id });
              });
          }
          
          // If we have existing data OR error response has data, don't show timeout error
          if (existingData && (existingData.rawData || existingData.transformedData || existingData.data)) {
            console.log('[SecurityMonitoring] Request timed out but keeping existing cached data', { siteId: site._id });
            setSecurityData(prev => ({
              ...prev,
              [site._id]: {
                ...prev[site._id],
                error: null, // Don't show timeout if we have cached data
                status: prev[site._id]?.status || 'cached'
              }
            }));
            return; // Exit early - don't show error
          } else if (hasErrorResponseData) {
            // Error response has data - process it instead of showing error
            console.log('[SecurityMonitoring] Request timed out but response has data, processing it', { siteId: site._id });
            const finalPayload = errorResponseData;
            
            // Extract and process the data (same logic as success case)
            let siteData = finalPayload?.data || finalPayload;
            const transformedFromApi = finalPayload?.transformedData || null;
            
            const hasMetaData = siteData && (siteData.ip || siteData.ip_address || siteData.hosting || siteData.runningOn || siteData.server || siteData.cms || siteData.powered_by);
            const hasTransformedData = transformedFromApi && (transformedFromApi.ip || transformedFromApi.hosting || transformedFromApi.runningOn || transformedFromApi.cms);
            
            if (hasMetaData || hasTransformedData) {
              // We have data, process it normally
              if (!siteData && transformedFromApi) {
                siteData = {
                  ip: transformedFromApi.ip || transformedFromApi.ip_address || '',
                  ip_address: transformedFromApi.ip_address || transformedFromApi.ip || '',
                  hosting: transformedFromApi.hosting || '',
                  runningOn: transformedFromApi.runningOn || transformedFromApi.server || '',
                  server: transformedFromApi.server || transformedFromApi.runningOn || '',
                  cms: transformedFromApi.cms || '',
                  powered_by: transformedFromApi.powered_by || transformedFromApi.poweredBy || ''
                };
              }
              
              const securityInfo = performSecurityChecks(site, siteData || {});
              const sucuriData = transformedFromApi || transformToSucuriFormat(site, siteData, securityInfo);
              
              // Extract meta from all sources
              const ipFromData = siteData?.ip || siteData?.ip_address || finalPayload?.ip || '';
              const hostingFromData = siteData?.hosting || finalPayload?.hosting || '';
              const runningOnFromData = siteData?.runningOn || siteData?.server || finalPayload?.runningOn || finalPayload?.server || '';
              
              if (sucuriData) {
                sucuriData.ip = sucuriData.ip || sucuriData.ip_address || transformedFromApi?.ip || ipFromData || '';
                sucuriData.ip_address = sucuriData.ip_address || sucuriData.ip || transformedFromApi?.ip_address || ipFromData || '';
                sucuriData.hosting = sucuriData.hosting || transformedFromApi?.hosting || hostingFromData || '';
                sucuriData.runningOn = sucuriData.runningOn || sucuriData.server || transformedFromApi?.runningOn || runningOnFromData || '';
                sucuriData.server = sucuriData.server || sucuriData.runningOn || transformedFromApi?.server || runningOnFromData || '';
                sucuriData.cms = sucuriData.cms || siteData?.cms || finalPayload?.cms || '';
                sucuriData.powered_by = sucuriData.powered_by || sucuriData.poweredBy || siteData?.powered_by || finalPayload?.powered_by || '';
              }
              
              setSecurityData(prev => ({
                ...prev,
                [site._id]: {
                  data: securityInfo,
                  rawData: siteData,
                  transformedData: sucuriData,
                  lastScan: finalPayload?.lastSync || finalPayload?.lastScan || new Date().toISOString(),
                  error: null, // Don't show error if we have data
                  status: finalPayload?.status || (finalPayload?.cached ? 'cached' : 'live')
                }
              }));
            } else {
              // No data in error response - but don't show error yet, wait for retry
              console.log('[SecurityMonitoring] No data in error response, waiting for retry', { siteId: site._id });
              // Don't set error yet - let retry attempt complete first
              // Only show error if retry also fails (handled below)
            }
          } else {
            // No existing data and no data in error response
            // CRITICAL: Don't show error immediately - wait a bit for retry or check if data exists
            // Only show error if we truly have no data after all attempts
            console.log('[SecurityMonitoring] Request failed, will attempt silent retry before showing error', { siteId: site._id, error: err.message });
            
            // Set a delayed error check - only show error if no data appears after retry
            setTimeout(() => {
              setSecurityData(prev => {
                const currentData = prev[site._id];
                // Only show error if we STILL have no data after retry attempt
                if (!currentData || (!currentData.rawData && !currentData.transformedData && !currentData.data)) {
                  console.log('[SecurityMonitoring] No data after retry, showing error', { siteId: site._id });
                  return {
                    ...prev,
                    [site._id]: {
                      ...(currentData || {}),
                      error: err.message?.includes('Timeout') ? 'Request timeout. Please try again.' : 'Failed to load data',
                      status: 'unavailable'
                    }
                  };
                } else {
                  // Data appeared from retry - don't show error
                  console.log('[SecurityMonitoring] Data appeared from retry, suppressing error', { siteId: site._id });
                  return {
                    ...prev,
                    [site._id]: {
                      ...currentData,
                      error: null, // Clear any error if data exists
                      status: currentData.status || 'cached'
                    }
                  };
                }
              });
            }, 16000); // Wait 16 seconds (15s retry timeout + 1s buffer)
          }
        });
      });
      
      // Hide loading immediately (progressive updates will show data as it arrives)
      setLoading(false);
      return; // Exit early - progressive updates handle the rest
    } catch (err) {
      console.error('Error fetching sites:', err);
      setError('Failed to load sites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSites();
    // Auto-refresh every 10 minutes
    const interval = setInterval(fetchSites, 600000);
    return () => clearInterval(interval);
  }, [fetchSites]);

  // Force sync via query param ?force=true
  useEffect(() => {
    if (forceParam && selectedSiteId && !forceSyncTriggered && !forceSyncInProgress) {
      const forceSync = async () => {
        try {
          setForceSyncInProgress(true);
          console.log('Force sync testing:');
          console.log(`POST /api/sites/${selectedSiteId}/sync-monitor?force=true`);
          await axios.post(`/api/sites/${selectedSiteId}/sync-monitor?force=true`, { force: true });
          await fetchSites();
          setForceSyncTriggered(true);
        } catch (err) {
          console.error('Force sync failed', err);
        } finally {
          setForceSyncInProgress(false);
        }
      };
      forceSync();
    }
  }, [forceParam, selectedSiteId, fetchSites, forceSyncTriggered, forceSyncInProgress]);

  // If force=true and no site selected yet, trigger once first site is known
  useEffect(() => {
    if (forceParam && sites.length > 0 && !selectedSiteId) {
      setSelectedSiteId(sites[0]._id);
    }
  }, [forceParam, sites, selectedSiteId]);

  // If siteId is provided in URL, select that site
  useEffect(() => {
    if (siteIdParam && sites.length > 0) {
      const siteExists = sites.find(s => s._id === siteIdParam);
      if (siteExists) {
        setSelectedSiteId(siteIdParam);
      }
    }
  }, [siteIdParam, sites]);

  // Perform basic security checks based on URL only (when site data is unavailable)
  const performBasicSecurityChecks = (site) => {
    const checks = {
      ssl: checkSSL(site.url),
      wpVersion: { status: 'unknown', message: 'Unable to check - site unavailable', recommendation: 'Ensure WordPress site is accessible' },
      phpVersion: { status: 'unknown', message: 'Unable to check - site unavailable', recommendation: 'Ensure WordPress site is accessible' },
      plugins: { status: 'unknown', message: 'Unable to check - site unavailable', recommendation: 'Ensure WebMonitor plugin is installed and activated' },
      theme: { status: 'unknown', message: 'Unable to check - site unavailable', recommendation: 'Ensure WordPress site is accessible' },
      filePermissions: checkFilePermissions(null),
      database: checkDatabase(null),
      loginSecurity: checkLoginSecurity(null),
      securityHeaders: checkSecurityHeaders(site.url),
      overall: { status: 'warning', score: 0, criticalCount: 0, warningCount: 0, issues: ['Site is currently unavailable'] }
    };
    
    // Calculate score based on available checks
    const sslStatus = checks.ssl.status;
    if (sslStatus === 'critical') {
      checks.overall.criticalCount = 1;
      checks.overall.issues.push('Site is not using HTTPS');
    }
    
    checks.overall.score = sslStatus === 'safe' ? 50 : 0; // Partial score if HTTPS is enabled
    
    return checks;
  };

  const performSecurityChecks = (site, siteData) => {
    const checks = {
      ssl: checkSSL(site.url),
      wpVersion: checkWordPressVersion(siteData?.wp_version),
      phpVersion: checkPHPVersion(siteData?.php_version),
      plugins: checkPlugins(siteData?.plugins || []),
      theme: checkTheme(siteData?.active_theme),
      filePermissions: checkFilePermissions(siteData),
      database: checkDatabase(siteData),
      loginSecurity: checkLoginSecurity(siteData),
      securityHeaders: checkSecurityHeaders(site.url),
      overall: { status: 'safe', score: 100, issues: [] }
    };

    // Calculate overall security score
    const issues = [];
    let criticalCount = 0;
    let warningCount = 0;

    Object.values(checks).forEach(check => {
      if (check && check.status) {
        if (check.status === 'critical') {
          criticalCount++;
          issues.push(...(check.issues || []));
        } else if (check.status === 'warning') {
          warningCount++;
          issues.push(...(check.issues || []));
        }
      }
    });

    // Calculate score (100 - (critical * 10) - (warning * 5))
    const score = Math.max(0, 100 - (criticalCount * 10) - (warningCount * 5));
    
    checks.overall = {
      status: criticalCount > 0 ? 'critical' : warningCount > 0 ? 'warning' : 'safe',
      score: score,
      criticalCount,
      warningCount,
      issues: issues.slice(0, 10) // Limit to top 10 issues
    };

    return checks;
  };

  const checkSSL = (url) => {
    if (!url) return { status: 'unknown', message: 'URL not available' };
    
    const isHttps = url.startsWith('https://');
    if (!isHttps) {
      return {
        status: 'warning', // Changed from 'critical' to 'warning' - missing HTTPS is Medium risk, not High/Critical
        message: 'Site is not using HTTPS',
        recommendation: 'Enable SSL certificate to encrypt data transmission'
      };
    }
    
    return {
      status: 'safe',
      message: 'HTTPS enabled',
      recommendation: null
    };
  };

  const checkWordPressVersion = (wpVersion) => {
    if (!wpVersion) return { status: 'unknown', message: 'WordPress version not available' };
    
    // Check if version is outdated (simplified check)
    const versionParts = wpVersion.split('.').map(Number);
    const major = versionParts[0] || 0;
    const minor = versionParts[1] || 0;
    
    // WordPress 6.0+ is generally considered secure if kept updated
    if (major < 5 || (major === 5 && minor < 8)) {
      return {
        status: 'critical',
        message: `WordPress ${wpVersion} is outdated`,
        recommendation: 'Update WordPress to the latest version for security patches'
      };
    }
    
    return {
      status: 'safe',
      message: `WordPress ${wpVersion}`,
      recommendation: 'Keep WordPress updated regularly'
    };
  };

  const checkPHPVersion = (phpVersion) => {
    if (!phpVersion) return { status: 'unknown', message: 'PHP version not available' };
    
    const versionParts = phpVersion.split('.').map(Number);
    const major = versionParts[0] || 0;
    const minor = versionParts[1] || 0;
    
    // PHP 8.0+ is recommended, 7.4 is minimum, below 7.4 is critical
    if (major < 7 || (major === 7 && minor < 4)) {
      return {
        status: 'critical',
        message: `PHP ${phpVersion} is outdated and unsupported`,
        recommendation: 'Update PHP to version 8.0 or higher for security and performance'
      };
    } else if (major === 7 && minor < 8) {
      return {
        status: 'warning',
        message: `PHP ${phpVersion} is getting outdated`,
        recommendation: 'Consider updating to PHP 8.0+ for better security'
      };
    }
    
    return {
      status: 'safe',
      message: `PHP ${phpVersion}`,
      recommendation: 'PHP version is up to date'
    };
  };

  const checkPlugins = (plugins) => {
    if (!plugins || plugins.length === 0) {
      return { status: 'unknown', message: 'No plugin data available' };
    }
    
    const outdatedPlugins = [];
    const inactivePlugins = [];
    
    plugins.forEach(plugin => {
      if (plugin.update && plugin.update !== false) {
        outdatedPlugins.push(plugin.name || plugin.plugin || 'Unknown');
      }
      if (plugin.active === false || plugin.status === 'inactive') {
        inactivePlugins.push(plugin.name || plugin.plugin || 'Unknown');
      }
    });
    
    if (outdatedPlugins.length > 0) {
      return {
        status: 'warning',
        message: `${outdatedPlugins.length} plugin(s) need updates`,
        issues: outdatedPlugins.map(name => `Update ${name}`),
        recommendation: 'Update outdated plugins to patch security vulnerabilities'
      };
    }
    
    if (inactivePlugins.length > 0) {
      return {
        status: 'safe',
        message: `${plugins.length} active plugins, ${inactivePlugins.length} inactive`,
        recommendation: 'Consider removing unused plugins to reduce attack surface'
      };
    }
    
    return {
      status: 'safe',
      message: `${plugins.length} plugins installed`,
      recommendation: 'All plugins are up to date'
    };
  };

  const checkTheme = (theme) => {
    if (!theme) return { status: 'unknown', message: 'Theme data not available' };
    
    if (theme.update && theme.update !== false) {
      return {
        status: 'warning',
        message: `Theme "${theme.name}" has updates available`,
        recommendation: 'Update theme to the latest version'
      };
    }
    
    return {
      status: 'safe',
      message: `Theme: ${theme.name}`,
      recommendation: 'Theme is up to date'
    };
  };

  const checkFilePermissions = (siteData) => {
    // This would typically require server-side checks
    // For now, we'll provide a general recommendation
    return {
      status: 'info',
      message: 'File permissions check',
      recommendation: 'Ensure wp-config.php has 400 or 600 permissions, directories 755, files 644'
    };
  };

  const checkDatabase = (siteData) => {
    // Basic database security recommendations
    return {
      status: 'info',
      message: 'Database security',
      recommendation: 'Use strong database passwords, limit database user privileges, regular backups'
    };
  };

  const checkLoginSecurity = (siteData) => {
    // Basic login security recommendations
    return {
      status: 'info',
      message: 'Login security',
      recommendation: 'Enable two-factor authentication, limit login attempts, use strong passwords'
    };
  };

  const checkSecurityHeaders = (url) => {
    // This would require actual HTTP header checks
    // For now, provide general recommendations
    return {
      status: 'info',
      message: 'Security headers',
      recommendation: 'Implement security headers: X-Frame-Options, X-Content-Type-Options, CSP, etc.'
    };
  };

  const scanSite = async (siteId) => {
    try {
      console.log('[SecurityMonitoring] Security scan requested', { siteId });
      setScanning({ ...scanning, [siteId]: true });
      const response = await axios.post(`/api/sites/${siteId}/sync-monitor`);

      console.log('[SecurityMonitoring] Scan response:', response.data);

      const payload = response.data;
      
      // Clean error message - remove HTTP status codes
      const cleanError = (msg) => {
        if (!msg) return null;
        let cleaned = msg.toString();
        cleaned = cleaned.replace(/HTTP\s+\d{3}:\s*[^\n]*/gi, '').trim();
        cleaned = cleaned.replace(/^\d{3}\s*[^\n]*/gi, '').trim();
        cleaned = cleaned.replace(/^OK$/i, '').trim();
        return cleaned || null;
      };
      
      // Extract site data first - check multiple possible locations
      const site = sites.find(s => s._id === siteId);
      let siteData = payload?.data;
      
      // Check if we have data in various formats
      const hasData = siteData && typeof siteData === 'object' && Object.keys(siteData).length > 0;
      const hasDirectData = payload?.wp_version || payload?.php_version || payload?.plugins || payload?.active_theme;
      
      // If no data field, check if payload itself contains site data
      if (!siteData && hasDirectData) {
        // Payload IS the site data
        siteData = payload;
        console.log('[SecurityMonitoring] Using payload as site data (direct fields)');
      }
      
        // Check if we have meaningful monitoring data
      const hasMonitoringData = siteData && (
        siteData.wp_version || 
        siteData.php_version || 
        siteData.plugins || 
        siteData.active_theme ||
        (typeof siteData === 'object' && Object.keys(siteData).length > 5)
      );
      
      console.log('[SecurityMonitoring] Data check:', {
        siteId,
        hasData,
        hasDirectData,
        hasMonitoringData,
        siteDataKeys: siteData ? Object.keys(siteData) : [],
        payloadKeys: Object.keys(payload || {}),
        success: payload?.success,
        status: payload?.status
      });
      
      // If we have data, process it regardless of success flag
      if (hasMonitoringData) {
        console.log('[SecurityMonitoring] ✅ Found monitoring data, processing...');
        
        // Process the data - perform security checks
        const securityInfo = performSecurityChecks(site, siteData);
        
        // Clean error message if present (for cached data warnings)
        const warningError = payload?.error ? cleanError(payload.error) : null;
        
        // Get raw site data from payload
        const rawSiteData = payload?.data || payload;
        
        setSecurityData({
          ...securityData,
          [siteId]: {
            data: securityInfo,
            rawData: rawSiteData, // Store raw data for Sucuri transformation
            lastScan: payload?.lastSync || payload?.lastScan || new Date().toISOString(),
            error: warningError, // Only show cleaned error if it's meaningful
            status: payload?.status || 'available'
          }
        });
        
        console.log('[SecurityMonitoring] ✅ Successfully processed security data');
        return;
      }
      
      // No monitoring data available - but we can still show basic security checks based on URL
      const siteUrl = site?.url || '';
      
      // Perform basic security checks based on URL only (when site is unavailable)
      console.log('[SecurityMonitoring] No monitoring data, performing basic URL-based security checks', { siteId, site });
      const basicSecurityInfo = performBasicSecurityChecks(site);
      console.log('[SecurityMonitoring] Basic security info generated', { 
        hasBasicInfo: !!basicSecurityInfo,
        sslStatus: basicSecurityInfo?.ssl?.status,
        overallScore: basicSecurityInfo?.overall?.score
      });
      
      let errorMsg = cleanError(payload.error) || cleanError(payload.message);
      
      // Provide more specific error messages based on site type and status
      if (payload.status === 'unavailable') {
        if (siteUrl.includes('.local') || siteUrl.includes('localhost') || siteUrl.includes('127.0.0.1')) {
          // More concise error message for local sites
          errorMsg = `Local site unavailable. Check: WordPress running, WebMonitor plugin active, plugin key configured, and site accessible from backend.`;
        } else {
          errorMsg = 'Site is currently unavailable. Please check if the WordPress site is online and the WebMonitor plugin is installed.';
        }
      } else if (!errorMsg) {
        errorMsg = 'Failed to scan site. Site may be unreachable or plugin not configured.';
      }
      
      // Add status_details information if available (but keep it concise)
      if (payload?.status_details) {
        const details = payload.status_details;
        if (details.failed_endpoints && details.failed_endpoints.length > 0) {
          // Show endpoint types instead of full URLs
          const endpointTypes = details.failed_endpoints.map(ep => {
            if (ep.includes('endpoint=sync')) return 'sync';
            if (ep.includes('endpoint=site-info')) return 'site-info';
            if (ep.includes('/wp-json/webmonitor/v1/')) return 'REST API';
            return 'endpoint';
          });
          errorMsg += ` Failed: ${[...new Set(endpointTypes)].join(', ')}`;
        }
        if (details.succeeded_endpoints && details.succeeded_endpoints.length > 0) {
          console.log('[SecurityMonitoring] Some endpoints succeeded:', details.succeeded_endpoints);
        }
      }
      
      console.log('[SecurityMonitoring] ❌ No monitoring data available, showing basic checks', { 
        siteId, 
        errorMsg, 
        payload,
        basicSecurityInfo: basicSecurityInfo ? 'exists' : 'missing',
        basicSecurityInfoKeys: basicSecurityInfo ? Object.keys(basicSecurityInfo) : []
      });
      
      // Show basic security info even when site is unavailable
      // This ensures users can see at least URL-based security checks (like SSL)
      if (basicSecurityInfo) {
        setSecurityData({
          ...securityData,
          [siteId]: {
            data: basicSecurityInfo,
            error: errorMsg, // Show error but also show basic security checks
            status: payload?.status || 'unavailable',
            isBasicCheck: true // Flag to indicate this is URL-based only
          }
        });
      } else {
        // Fallback if basicSecurityInfo failed to generate
        console.error('[SecurityMonitoring] Failed to generate basic security info');
        setSecurityData({
          ...securityData,
          [siteId]: {
            error: errorMsg,
            data: null,
            status: payload?.status || 'unavailable'
          }
        });
      }
    } catch (err) {
      console.error('[SecurityMonitoring] Error scanning site:', {
        siteId,
        error: err,
        response: err.response?.data,
        status: err.response?.status,
        message: err.message
      });
      
      // Extract meaningful error message, avoiding HTTP status codes
      let errorMessage = 'Failed to scan site';
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message && !err.message.includes('HTTP') && !err.message.match(/^\d{3}/)) {
        errorMessage = err.message;
      }
      
      // Remove HTTP status messages
      errorMessage = errorMessage.replace(/HTTP\s+\d{3}:\s*[^\n]*/gi, '').trim();
      errorMessage = errorMessage.replace(/^\d{3}\s*[^\n]*/gi, '').trim();
      
      // Provide more specific error messages based on status code
      if (err.response?.status === 404) {
        errorMessage = 'Site not found. Please verify the site exists.';
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        errorMessage = 'Authentication failed. Please check site credentials.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please try again later or check site configuration.';
      } else if (err.response?.status === 502 || err.response?.status === 503) {
        errorMessage = 'Site is unreachable. Please check if the WordPress site is online.';
      } else if (err.response?.status === 504) {
        errorMessage = 'Request timeout. The site may be slow or unresponsive.';
      }
      
      if (!errorMessage || errorMessage === 'Failed to scan site') {
        errorMessage = 'Failed to scan site. Please check the browser console for details.';
      }
      
      alert(errorMessage);
      setSecurityData({
        ...securityData,
        [siteId]: { error: errorMessage, data: null }
      });
    } finally {
      setScanning({ ...scanning, [siteId]: false });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'critical': return '#dc3545';
      case 'warning': return '#ffc107';
      case 'safe': return '#28a745';
      case 'info': return '#17a2b8';
      default: return '#999';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'critical': return '🔴';
      case 'warning': return '🟡';
      case 'safe': return '🟢';
      case 'info': return 'ℹ️';
      default: return '⚪';
    }
  };

  const buildVaptOverview = (sitesList = [], data = {}) => {
    const overallSeverity = { critical: 0, high: 0, medium: 0, low: 0 };

    const malware = { critical: 0, high: 0, medium: 0, low: 0, total: 0 };
    const app = { critical: 0, high: 0, medium: 0, low: 0, total: 0 };
    const system = { critical: 0, high: 0, medium: 0, low: 0, total: 0 };

    sitesList.forEach((site) => {
      const entry = data[site._id] || {};
      const rawSiteData = entry.rawData || {};
      const sucuriDataBase = entry.transformedData || {};
      // Always fall back to a computed Sucuri-format object, even if we only have basic site info.
      const fallbackSucuri = transformToSucuriFormat(site, rawSiteData, entry.data);
      const sucuriData = {
        ...fallbackSucuri,
        ...sucuriDataBase,
        // Preserve security data from backend or fallback
        security_headers: sucuriDataBase.security_headers || fallbackSucuri.security_headers || {},
        hardening: sucuriDataBase.hardening || fallbackSucuri.hardening || {},
        security_checks: sucuriDataBase.security_checks || fallbackSucuri.security_checks || {}
      };
      const overall = entry.data?.overall || {};
      const criticalIssues = overall.criticalCount || 0;
      const warningIssues = overall.warningCount || 0;
      const outdatedCount = Array.isArray(sucuriData?.outdated_software) ? sucuriData.outdated_software.length : 0;

      // OVERALL: use risk score bands to categorize each site
      const riskScore = Number(sucuriData?.risk_score || 0);
      if (riskScore > 80) {
        overallSeverity.critical += 1;
      } else if (riskScore > 60) {
        overallSeverity.high += 1;
      } else if (riskScore > 40) {
        overallSeverity.medium += 1;
      } else {
        overallSeverity.low += 1; // includes minimal / low risk
      }

      // MALWARE CATEGORY: preserve backend malware counts if present
      const malwareCount = Number(
        sucuriData?.security_checks?.malware_count ||
          (sucuriData?.security_checks?.malware_detected ? 1 : 0)
      );
      if (malwareCount > 0) {
        // Treat any detected malware as critical for that site
        malware.critical += 1;
        malware.total += malwareCount;
      } else {
        malware.low += 1;
      }

      // APPLICATION AUDIT CATEGORY: use outdated software count
      if (outdatedCount > 0) {
        app.total += outdatedCount;
        if (outdatedCount >= 5) {
          app.high += 1;
        } else if (outdatedCount >= 2) {
          app.medium += 1;
        } else {
          app.low += 1;
        }
      } else {
        app.low += 1;
      }

      // SYSTEM / VAPT CATEGORY: use critical + warning issues
      const systemIssues = criticalIssues + warningIssues;
      if (systemIssues > 0) {
        system.total += systemIssues;
        if (criticalIssues > 0) {
          system.critical += 1;
        } else if (warningIssues >= 2) {
          system.high += 1;
        } else {
          system.medium += 1;
        }
      } else {
        system.low += 1;
      }
    });

    return {
      overallSeverity,
      malware,
      app,
      system
    };
  };

  // Show all sites or just selected one based on view mode
  // For now, show all sites so user can monitor all websites
  const filteredSites = selectedSiteId 
    ? sites.filter(site => site._id === selectedSiteId)
    : sites; // Show all sites if none selected

  const vaptOverview = useMemo(
    () => buildVaptOverview(filteredSites, securityData),
    [filteredSites, securityData]
  );

  // Active site for AppSec data (Invicti-style sections)
  const activeSiteId = selectedSiteId || (sites.length > 0 ? sites[0]._id : null);

  // Track active scans for loading state
  const hasActiveScans = useMemo(() => {
    return appsecScans.some(s => s.status === 'running' || s.status === 'queued');
  }, [appsecScans.map(s => `${s._id}-${s.status}`).join(',')]);

  // Load AppSec (Invicti-style) data for currently selected site
  // IMPORTANT: Avoid constant full reloads that cause blinking in the UI.
  // - Do a full load with spinner when the site/tab changes
  // - Only background-refresh (without spinner) when there are active scans AND we're on the Live Scans tab
  useEffect(() => {
    const loadAppSec = async (showLoading = true) => {
      if (!activeSiteId) {
        setAppsecScans([]);
        setAppsecVulns([]);
        return;
      }
      try {
        if (showLoading) {
          setAppsecLoading(true);
        }
        setAppsecError(null);
        const [scansRes, vulnsRes] = await Promise.all([
          axios.get(`/api/appsec/scans?siteId=${activeSiteId}`),
          axios.get(`/api/appsec/vulnerabilities?siteId=${activeSiteId}`),
        ]);
        setAppsecScans(scansRes.data?.scans || []);
        setAppsecVulns(vulnsRes.data?.vulnerabilities || []);
      } catch (err) {
        console.error('[SecurityMonitoring] Failed to load AppSec data', err);
        setAppsecError(err.response?.data?.message || err.message || 'Failed to load AppSec data');
        setAppsecScans([]);
        setAppsecVulns([]);
      } finally {
        if (showLoading) {
          setAppsecLoading(false);
        }
      }
    };

    // Initial load when site or tab changes
    if (activeSiteId) {
      loadAppSec(true);
    } else {
      setAppsecScans([]);
      setAppsecVulns([]);
    }
    
    // Background refresh removed - Live Scans tab was removed
  }, [axios, activeSiteId, hasActiveScans, appsecTab]);

  // Count validation for vulnerability data
  useEffect(() => {
    const validateCounts = async () => {
      if (!activeSiteId) return;

      try {
        // Get real counts from vulnerabilities
        const malwareCount = appsecVulns.filter(v => 
          v.type === 'malware' || v.sources?.includes('malware')
        ).length;
        const vulnerabilityCount = appsecVulns.filter(v => 
          v.status === 'open' && v.sources?.includes('dast')
        ).length;
        const systemCount = appsecVulns.filter(v => 
          v.sources?.includes('system') && v.status === 'open'
        ).length;

      } catch (err) {
        // Silently fail - validation is non-critical
        console.debug('[Count Validation] Failed to validate counts', err);
      }
    };

    if (activeSiteId && appsecVulns.length > 0) {
      validateCounts();
    }
  }, [activeSiteId, appsecVulns, axios]);

  if (loading) {
    return (
      <div className="dashboard-container">
        <Sidebar />
        <div className="security-monitoring-page">
          <div className="loading">Loading security data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Sidebar />
      <div className="security-monitoring-page">
        <div className="security-header">
          <div>
            <h1>Security</h1>
            <p className="subtitle">Monitor security status and vulnerabilities for your website</p>
          </div>
          <div className="header-actions">
            <select
              value={selectedSiteId}
              onChange={(e) => setSelectedSiteId(e.target.value)}
              className="site-select"
            >
              <option value="">All Websites</option>
              {sites.map(site => (
                <option key={site._id} value={site._id}>
                  {site.name || site.url}
                </option>
              ))}
            </select>
            <button onClick={() => fetchSites(false)} className="btn-refresh-all">
              🔄 Refresh All
            </button>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            {error}
          </div>
        )}


        {/* VAPT summary section removed at user request */}
        <div className="sucuri-style-layout">
          {(forceParam && (!forceSyncTriggered || forceSyncInProgress)) && (
            <div className="loading-state">Force syncing latest data...</div>
          )}
          {(!selectedSiteId && (forceSyncInProgress || loading)) && (
            <div className="loading-state">Synchronizing latest data...</div>
          )}
          
          {/* Show all sites in grid when no site is selected */}
          {!selectedSiteId && filteredSites.length > 0 && (
            <div className="sites-grid">
              {filteredSites.map((site) => {
                const entry = securityData[site._id] || {};
                const rawSiteData = entry.rawData || {};
                const sucuriDataBase = entry.transformedData || {};
                const fallbackSucuri = transformToSucuriFormat(site, rawSiteData, entry.data);
                
                // Extract from all possible sources with deep fallback
                const ipFromRaw = rawSiteData?.ip || rawSiteData?.ip_address || rawSiteData?.ipAddress || '';
                const hostingFromRaw = rawSiteData?.hosting || rawSiteData?.host_provider || rawSiteData?.hosting_provider || '';
                const runningOnFromRaw = rawSiteData?.runningOn || rawSiteData?.server || rawSiteData?.serverSoftware || rawSiteData?.running_on || '';
                const cmsFromRaw = rawSiteData?.cms || rawSiteData?.cms_name || '';
                const poweredByFromRaw = rawSiteData?.powered_by || rawSiteData?.poweredBy || '';
                
                // Log what we're extracting for debugging
                console.log('[SecurityMonitoring] Grid card data extraction', {
                  siteId: site._id,
                  siteName: site.name,
                  hasEntry: !!entry,
                  hasRawData: !!entry.rawData,
                  hasTransformedData: !!entry.transformedData,
                  rawDataKeys: Object.keys(rawSiteData),
                  transformedDataKeys: Object.keys(sucuriDataBase),
                  ipFromRaw,
                  hostingFromRaw,
                  runningOnFromRaw,
                  ipFromTransformed: sucuriDataBase?.ip || sucuriDataBase?.ip_address || '',
                  hostingFromTransformed: sucuriDataBase?.hosting || '',
                  runningOnFromTransformed: sucuriDataBase?.runningOn || sucuriDataBase?.server || ''
                });
                
                // CRITICAL: Calculate risk score from frontend (fallbackSucuri) - this ensures healthy sites show Low/Medium
                const frontendRiskScore = fallbackSucuri?.risk_score ?? 0;
                const frontendRiskLevel = fallbackSucuri?.risk_level ?? 'low';
                
                console.log('[SecurityMonitoring] Risk score calculation', {
                  siteId: site._id,
                  siteName: site.name,
                  frontendRiskScore,
                  frontendRiskLevel,
                  backendRiskScore: sucuriDataBase?.risk_score,
                  backendRiskLevel: sucuriDataBase?.risk_level,
                  usingFrontend: !!fallbackSucuri?.risk_score
                });
                
                const sucuriData = {
                  ...fallbackSucuri,
                  ...sucuriDataBase,
                  // Priority: transformedData > rawData > fallback > empty string
                  ip: (sucuriDataBase?.ip || sucuriDataBase?.ip_address || ipFromRaw || fallbackSucuri?.ip || fallbackSucuri?.ip_address || '').trim() || '',
                  ip_address: (sucuriDataBase?.ip_address || sucuriDataBase?.ip || ipFromRaw || fallbackSucuri?.ip_address || fallbackSucuri?.ip || '').trim() || '',
                  hosting: (sucuriDataBase?.hosting || hostingFromRaw || fallbackSucuri?.hosting || '').trim() || '',
                  runningOn: (sucuriDataBase?.runningOn || sucuriDataBase?.server || runningOnFromRaw || fallbackSucuri?.runningOn || fallbackSucuri?.server || '').trim() || '',
                  server: (sucuriDataBase?.server || sucuriDataBase?.runningOn || runningOnFromRaw || fallbackSucuri?.server || fallbackSucuri?.runningOn || '').trim() || '',
                  cms: (sucuriDataBase?.cms || cmsFromRaw || fallbackSucuri?.cms || '').trim() || '',
                  powered_by: (sucuriDataBase?.powered_by || sucuriDataBase?.poweredBy || poweredByFromRaw || fallbackSucuri?.powered_by || fallbackSucuri?.poweredBy || '').trim() || '',
                  // CRITICAL: Preserve security_headers and hardening from backend or fallback
                  security_headers: sucuriDataBase?.security_headers || fallbackSucuri?.security_headers || {},
                  hardening: sucuriDataBase?.hardening || fallbackSucuri?.hardening || {},
                  security_checks: sucuriDataBase?.security_checks || fallbackSucuri?.security_checks || {},
                  // CRITICAL: ALWAYS use frontend-calculated risk_score (from fallbackSucuri) instead of backend
                  // This ensures healthy sites show Low/Medium, not High
                  risk_score: frontendRiskScore,
                  risk_level: frontendRiskLevel
                };
                
                const riskScore = frontendRiskScore;
                const riskLevel = frontendRiskLevel;
                
                return (
                  <div key={site._id} className="site-security-card" onClick={() => setSelectedSiteId(site._id)}>
                    <div className="site-card-header">
                      <h3>{site.name || site.url}</h3>
                      <div className="site-url">{site.url}</div>
                      {/* CRITICAL: NEVER show badges if we have ANY data - completely hide them */}
                      {/* Badges are completely disabled - we don't want any button-like structures showing */}
                      {null}
                    </div>
                    
                    <div className="site-card-risk">
                      <RiskMeter riskScore={riskScore} riskLevel={riskLevel} />
                    </div>
                    
                    <div className="site-card-details">
                      <div className="detail-item">
                        <span className="detail-label">IP address:</span>
                        <span className="detail-value">{(sucuriData.ip || sucuriData.ip_address || '').trim() || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Hosting:</span>
                        <span className="detail-value">{(sucuriData.hosting || '').trim() || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Running on:</span>
                        <span className="detail-value">{(sucuriData.runningOn || sucuriData.server || '').trim() || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">CMS:</span>
                        <span className="detail-value">{(sucuriData.cms || '').trim() || 'Unknown'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Powered by:</span>
                        <span className="detail-value">{(sucuriData.powered_by || sucuriData.poweredBy || '').trim() || 'Unknown'}</span>
                      </div>
                    </div>
                    
                    {/* CRITICAL: NEVER show error messages if we have ANY data */}
                    {(() => {
                      const hasAnyData = !!(entry.rawData || entry.transformedData || entry.data || 
                                           sucuriData?.ip || sucuriData?.hosting || sucuriData?.runningOn || 
                                           sucuriData?.cms || rawSiteData?.ip || rawSiteData?.hosting);
                      // Only show error if we have NO data at all
                      if (entry.error && !hasAnyData) {
                        return (
                          <div className="site-card-error">
                            <small>{entry.error}</small>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    
                    <div className="site-card-footer">
                      <button 
                        className="btn-view-details"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSiteId(site._id);
                        }}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Show detailed view when a site is selected */}
          {selectedSiteId && filteredSites.map((site) => {
            const entry = securityData[site._id] || {};
            const securityInfo = entry.data;
            const hasError = !!entry.error;
            const hasSecurityInfo = !!securityInfo;
            const lastScan = entry.lastScan || null;
            
            // Get raw site data for transformation
            const rawSiteData = entry.rawData || {};
            
            // Transform to Sucuri format with fallback backfill
            const fallbackSucuri = transformToSucuriFormat(site, rawSiteData, securityInfo);
            const sucuriDataBase = entry.transformedData || {};
            
            // Extract from rawData as well (in case it's in the main data object)
            const ipFromRaw = rawSiteData?.ip || rawSiteData?.ip_address || '';
            const hostingFromRaw = rawSiteData?.hosting || '';
            const runningOnFromRaw = rawSiteData?.runningOn || rawSiteData?.server || '';
            
            // CRITICAL: Calculate risk score from frontend (fallbackSucuri) - this ensures healthy sites show Low/Medium
            const frontendRiskScore = fallbackSucuri?.risk_score ?? 0;
            const frontendRiskLevel = fallbackSucuri?.risk_level ?? 'low';
            
            console.log('[SecurityMonitoring] Detailed view risk score calculation', {
              siteId: site._id,
              siteName: site.name,
              frontendRiskScore,
              frontendRiskLevel,
              backendRiskScore: sucuriDataBase?.risk_score,
              backendRiskLevel: sucuriDataBase?.risk_level,
              usingFrontend: !!fallbackSucuri?.risk_score
            });
            
            const sucuriData = {
              ...fallbackSucuri,
              ...sucuriDataBase,
              // Prioritize: transformedData > rawData > fallback
              ip: sucuriDataBase.ip || sucuriDataBase.ip_address || ipFromRaw || fallbackSucuri.ip || fallbackSucuri.ip_address || '',
              ip_address: sucuriDataBase.ip_address || sucuriDataBase.ip || ipFromRaw || fallbackSucuri.ip_address || fallbackSucuri.ip || '',
              hosting: sucuriDataBase.hosting || hostingFromRaw || fallbackSucuri.hosting || '',
              runningOn: sucuriDataBase.runningOn || sucuriDataBase.server || runningOnFromRaw || fallbackSucuri.runningOn || fallbackSucuri.server || '',
              server: sucuriDataBase.server || sucuriDataBase.runningOn || runningOnFromRaw || fallbackSucuri.server || fallbackSucuri.runningOn || '',
              cms: sucuriDataBase.cms || fallbackSucuri.cms || '',
              powered_by: sucuriDataBase.powered_by || sucuriDataBase.poweredBy || fallbackSucuri.powered_by || fallbackSucuri.poweredBy || '',
              poweredBy: sucuriDataBase.poweredBy || sucuriDataBase.powered_by || fallbackSucuri.poweredBy || fallbackSucuri.powered_by || '',
              // CRITICAL: Explicitly preserve security_headers and hardening from backend or fallback
              security_headers: sucuriDataBase.security_headers || fallbackSucuri.security_headers || {},
              hardening: sucuriDataBase.hardening || fallbackSucuri.hardening || {},
              security_checks: sucuriDataBase.security_checks || fallbackSucuri.security_checks || {},
              // CRITICAL: ALWAYS use frontend-calculated risk_score (from fallbackSucuri) instead of backend
              // This ensures healthy sites show Low/Medium, not High
              risk_score: frontendRiskScore,
              risk_level: frontendRiskLevel
            };
            
            console.log('[SecurityMonitoring] Final sucuriData merge', {
              siteId: site._id,
              ip: sucuriData.ip,
              hosting: sucuriData.hosting,
              runningOn: sucuriData.runningOn,
              sources: {
                transformed: { ip: sucuriDataBase.ip, hosting: sucuriDataBase.hosting, runningOn: sucuriDataBase.runningOn },
                raw: { ip: ipFromRaw, hosting: hostingFromRaw, runningOn: runningOnFromRaw },
                fallback: { ip: fallbackSucuri.ip, hosting: fallbackSucuri.hosting, runningOn: fallbackSucuri.runningOn }
              }
            });
            
            // If no data available, show error
            if (!hasSecurityInfo && !sucuriData) {
              return (
                <div key={site._id} className="security-scan-container">
                  <div className="scan-header">
                    <button 
                      onClick={() => {
                        // If siteId is in URL params, navigate back to that site's details page
                        // Otherwise, clear selection to show grid view or go back in browser history
                        if (siteIdParam) {
                          navigate(`/sites/${siteIdParam}`);
                        } else {
                          // Clear selection to show all sites grid
                          setSelectedSiteId('');
                        }
                      }} 
                      className="back-link"
                    >
                      ← Back
                    </button>
                    <h2 className="scan-url">{site.url}</h2>
                  </div>
                  <div className="card-error">
                    <p style={{ whiteSpace: 'pre-line' }}>{entry.error || 'Failed to load security data'}</p>
                    <button
                      onClick={() => scanSite(site._id)}
                      className="btn-scan-now"
                      disabled={scanning[site._id]}
                    >
                      {scanning[site._id] ? 'Scanning...' : 'Scan Now'}
                    </button>
                  </div>
                </div>
              );
            }

            if (forceSyncInProgress && site._id === selectedSiteId) {
              return (
                <div key={site._id} className="security-scan-container">
                  <div className="scan-header">
                    <button 
                      onClick={() => {
                        // If siteId is in URL params, navigate back to that site's details page
                        // Otherwise, clear selection to show grid view or go back in browser history
                        if (siteIdParam) {
                          navigate(`/sites/${siteIdParam}`);
                        } else {
                          // Clear selection to show all sites grid
                          setSelectedSiteId('');
                        }
                      }} 
                      className="back-link"
                    >
                      ← Back
                    </button>
                    <h2 className="scan-url">{site.url}</h2>
                  </div>
                  <div className="loading-state">Force syncing...</div>
                </div>
              );
            }

            return (
              <div key={site._id} className="security-scan-container">
                {/* Header with back button and URL */}
                <div className="scan-header">
                  <button 
                    onClick={() => {
                      // If siteId is in URL params, navigate back to that site's details page
                      // Otherwise, clear selection to show grid view
                      if (siteIdParam) {
                        navigate(`/sites/${siteIdParam}`);
                      } else {
                        // Clear selection to show all sites grid
                        setSelectedSiteId('');
                      }
                    }} 
                    className="back-link"
                  >
                    ← Back
                  </button>
                  <h2 className="scan-url">{site.url}</h2>
                </div>

                {/* Summary Banner */}
                {sucuriData && <SummaryBanner scanData={sucuriData} />}

                {/* Two Column Layout */}
                <div className="scan-layout-grid">
                  {/* Left Column */}
                  <div className="scan-left-column">
                    {/* Website Summary Card */}
                    {sucuriData && <WebsiteSummaryCard site={site} scanData={sucuriData} />}

                    {/* Risk Meter */}
                    {sucuriData && (
                      <RiskMeter 
                        riskScore={sucuriData.risk_score || 0} 
                        riskLevel={sucuriData.risk_level || 'low'} 
                      />
                    )}

                    {/* Outdated Software */}
                    {sucuriData && <OutdatedSoftwareCard scanData={sucuriData} />}

                    {/* Security Status Card */}
                    {sucuriData && <SecurityStatusCard scanData={sucuriData} />}
                  </div>

                  {/* Right Column */}
                  <div className="scan-right-column">
                    {/* Blacklist Status Card */}
                    {sucuriData && <BlacklistStatusCard scanData={sucuriData} />}

                    {/* Hardening Improvements */}
                    {sucuriData && <HardeningImprovementsCard scanData={sucuriData} />}
                  </div>
                </div>

                {/* Error warning if present - but not for timeouts if we have valid data */}
                {entry.error && hasSecurityInfo && !entry.error.includes('Timeout') && !entry.error.includes('timeout') && (
                  <div className="card-error warning-inline">
                    <p style={{ whiteSpace: 'pre-line' }}>{entry.error}</p>
                  </div>
                )}

                {/* Last scan info */}
                {lastScan && (
                  <div className="scan-footer">
                    <span>Last scan: {new Date(lastScan).toLocaleString()}</span>
                    <button
                      onClick={() => scanSite(site._id)}
                      className="btn-rescan"
                      disabled={scanning[site._id]}
                    >
                      {scanning[site._id] ? 'Scanning...' : 'Rescan Site'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {filteredSites.length === 0 && (
            <div className="empty-state">
              <p>{selectedSiteId ? 'Selected site not found' : 'Please select a website from the dropdown above'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecurityMonitoring;

